import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ActivityReportModal from '../components/ActivityReportModal';
import ReportBuilderModal from '../components/ReportBuilderModal';
import ByPersonCard from '../components/ByPersonCard';
import QuarterlyByPersonCard from '../components/QuarterlyByPersonCard';
import { Card, Label } from '../components/StatsUI';

// Admins see the full team dashboard; everyone else sees their personal view —
// their own numbers against the co-worker average, never other individuals'.
export default function StatsPage() {
  const { user } = useAuth();
  if (user?.role === 'admin') return <AdminStats />;
  return <MyStats role={user?.role} />;
}

function AdminStats() {
  const [cad, setCad] = useState(null);
  const [rec, setRec] = useState(null);
  const [gis, setGis] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [flex, setFlex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);   // { dept, title } → activity report modal
  const [builder, setBuilder] = useState(false); // custom report builder modal

  useEffect(() => {
    Promise.all([
      api.get('/jobs/stats/summary'),
      api.get('/records/stats/summary'),
      api.get('/gis/stats/summary'),
    ]).then(([cadRes, recRes, gisRes]) => {
      setCad(cadRes.data);
      setRec(recRes.data);
      setGis(gisRes.data);
    }).finally(() => setLoading(false));
    // Forecast hits Site Tracker live, so it loads separately — a slow or failed
    // ST call shouldn't block (or blank) the rest of the stats page.
    api.get('/jobs/stats/forecast').then((res) => setForecast(res.data)).catch(() => {});
    api.get('/jobs/stats/flex-schedule').then((res) => setFlex(res.data)).catch(() => {});
  }, []);

  if (loading) return <Layout><p className="text-gray-500 text-sm p-6">Loading stats…</p></Layout>;

  const maxCadMonth = cad ? Math.max(...(cad.byMonth || []).map(m => Number(m.count)), 1) : 1;
  const maxRecMonth = rec ? Math.max(...(rec.byMonth || []).map(m => Number(m.count)), 1) : 1;
  const maxGisMonth = gis ? Math.max(...(gis.byMonth || []).map(m => Number(m.count)), 1) : 1;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">Stats</h1>
            <p className="text-gray-400 text-sm mt-1">CAD, Records &amp; Geospatial team performance</p>
          </div>
          <button
            onClick={() => setBuilder(true)}
            className="shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded transition"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            Generate Report
          </button>
        </div>

        {report && (
          <ActivityReportModal dept={report.dept} title={report.title} onClose={() => setReport(null)} />
        )}
        {builder && <ReportBuilderModal onClose={() => setBuilder(false)} />}

        {/* ── CAD SECTION ── */}
        <Section title="CAD" action={<ReportButton onClick={() => setReport({ dept: 'cad', title: 'CAD' })} />}>
          {cad && <>
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Pending',     value: cad.summary?.pending,              color: 'text-yellow-400' },
                { label: 'In Progress', value: cad.summary?.in_progress,          color: 'text-blue-400' },
                { label: 'Rush',        value: cad.summary?.rush,                 color: 'text-red-400' },
                { label: '3rd Party',   value: cad.summary?.third_party,          color: 'text-purple-400' },
              ].map(c => <StatCard key={c.label} {...c} />)}
            </div>

            {/* Pressure forecast — incoming work from field jobs scheduled in Site Tracker */}
            <ForecastCard forecast={forecast} />

            <div className="grid grid-cols-2 gap-4">
              {/* Completions this/last month */}
              <Card>
                <Label>Completions</Label>
                <div className="flex items-end gap-6 mt-3">
                  <BigNum value={cad.summary?.completed_this_month} sub="This month" />
                  <BigNum value={cad.summary?.completed_last_month} sub="Last month" dim />
                  <div className="ml-auto text-right text-sm text-gray-400">
                    <p>{cad.summary?.loaded ?? 0} Loaded</p>
                    <p>{cad.summary?.shipped ?? 0} Shipped</p>
                  </div>
                </div>
              </Card>

              {/* By person, month by month */}
              <ByPersonCard base="/jobs" initialRows={cad.byPerson} />
            </div>

            {/* Per-person quarterly totals: completions, rush, reviews, QC tickets filed */}
            <QuarterlyByPersonCard />

            {/* Monthly bar chart */}
            <Card>
              <Label>Completions — Last 6 Months</Label>
              <BarChart rows={cad.byMonth} max={maxCadMonth} />
            </Card>

            {/* Avg days */}
            <Card>
              <Label>Avg Days to Completion</Label>
              <AvgDaysGrid rows={cad.avgDays} />
            </Card>

            {/* Third-party review workload per tech */}
            <Card>
              <Label>Third-Party Reviews by Person</Label>
              {!cad.reviews?.length ? (
                <p className="text-gray-500 text-sm mt-3">No reviews tracked yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {cad.reviews.map(p => (
                    <div key={p.name} className="flex items-center justify-between text-xs">
                      <span className="text-gray-300 w-24">{p.name}</span>
                      <div className="flex gap-4">
                        <span className="text-green-400">{p.completed_this_month} this month</span>
                        <span className="text-blue-400">{p.active} active</span>
                        <span className="text-gray-400">{p.avg_days ?? '—'} days avg</span>
                        <span className="text-gray-500">{p.total} all time</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>}
        </Section>

        {/* ── RECORDS SECTION ── */}
        <Section
          title="Records"
          action={
            <div className="flex gap-2">
              <ReportButton label="Records Activity" onClick={() => setReport({ dept: 'records', title: 'Records' })} />
              <ReportButton label="Digs Activity" onClick={() => setReport({ dept: 'digs', title: 'Digs' })} />
            </div>
          }
        >
          {rec && <>
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Pending',     value: rec.summary?.pending,              color: 'text-yellow-400' },
                { label: 'In Progress', value: rec.summary?.in_progress,          color: 'text-blue-400' },
                { label: 'Complete (mo)',value: rec.summary?.completed_this_month, color: 'text-green-400' },
                { label: 'Civil Grid',  value: rec.summary?.civil_grid,           color: 'text-purple-400' },
              ].map(c => <StatCard key={c.label} {...c} />)}
            </div>

            {/* Ken Smith flex schedule — rolling 5-day Site Tracker calendar */}
            <Card>
              <Label>Ken Smith — Flex Schedule (Next 5 Days)</Label>
              <p className="text-xs text-gray-500 mt-1">Site Tracker field bookings — open days are available for office work</p>
              {flex ? (
                <FlexCalendar days={flex.days} />
              ) : (
                <p className="text-gray-500 text-sm mt-4">Loading schedule from Site Tracker…</p>
              )}
            </Card>

            <div className="grid grid-cols-2 gap-4">
              {/* This month vs last */}
              <Card>
                <Label>Completions</Label>
                <div className="flex items-end gap-6 mt-3">
                  <BigNum value={rec.summary?.completed_this_month} sub="This month" />
                  <BigNum value={rec.summary?.completed_last_month} sub="Last month" dim />
                </div>
              </Card>

              {/* By person, month by month */}
              <ByPersonCard base="/records" variant="records" initialRows={rec.byPerson} />
            </div>

            {/* Monthly chart */}
            <Card>
              <Label>Completions — Last 6 Months</Label>
              <BarChart rows={rec.byMonth} max={maxRecMonth} />
            </Card>

            {/* Avg days */}
            <Card>
              <Label>Avg Days to Completion</Label>
              <AvgDaysGrid rows={rec.avgDays} />
            </Card>
          </>}
        </Section>

        {/* ── GEOSPATIAL SECTION ── */}
        <Section title="Geospatial" action={<ReportButton onClick={() => setReport({ dept: 'gis', title: 'Geospatial' })} />}>
          {gis && <>
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Pending',      value: gis.summary?.pending,              color: 'text-yellow-400' },
                { label: 'In Progress',  value: gis.summary?.in_progress,          color: 'text-blue-400' },
                { label: 'Rush',         value: gis.summary?.rush,                 color: 'text-red-400' },
                { label: 'Complete (mo)',value: gis.summary?.completed_this_month, color: 'text-green-400' },
              ].map(c => <StatCard key={c.label} {...c} />)}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* This month vs last */}
              <Card>
                <Label>Completions</Label>
                <div className="flex items-end gap-6 mt-3">
                  <BigNum value={gis.summary?.completed_this_month} sub="This month" />
                  <BigNum value={gis.summary?.completed_last_month} sub="Last month" dim />
                </div>
              </Card>

              {/* By person, month by month */}
              <ByPersonCard base="/gis" initialRows={gis.byPerson} />
            </div>

            {/* Monthly chart */}
            <Card>
              <Label>Completions — Last 6 Months</Label>
              <BarChart rows={gis.byMonth} max={maxGisMonth} />
            </Card>

            {/* Avg days */}
            <Card>
              <Label>Avg Days to Completion</Label>
              <AvgDaysGrid rows={gis.avgDays} />
            </Card>
          </>}
        </Section>
      </div>
    </Layout>
  );
}

/* ── Personal (non-admin) view ── */

function MyStats({ role }) {
  const isCad = role === 'cad';
  const [me, setMe] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(isCad ? '/jobs/stats/me' : '/records/stats/me')
      .then((res) => setMe(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Forecast hits Site Tracker live — load it separately so it can't blank the page
    if (isCad) api.get('/jobs/stats/forecast').then((res) => setForecast(res.data)).catch(() => {});
  }, [isCad]);

  if (loading) return <Layout><p className="text-gray-500 text-sm p-6">Loading stats…</p></Layout>;
  if (!me) return <Layout><p className="text-gray-500 text-sm p-6">Couldn't load your stats. Try again later.</p></Layout>;

  const s = me.summary || {};
  const cards = isCad
    ? [
        { label: 'Completed This Month', value: s.completed_this_month, color: 'text-green-400' },
        { label: 'In Progress',          value: s.in_progress,          color: 'text-blue-400' },
        { label: 'Avg Days',             value: s.avg_days ?? '—',      color: 'text-white' },
        { label: 'Reviews This Month',   value: s.reviews_this_month,   color: 'text-purple-400' },
      ]
    : [
        { label: 'Completed This Month', value: s.completed_this_month, color: 'text-green-400' },
        { label: 'In Progress',          value: s.in_progress,          color: 'text-blue-400' },
        { label: 'Pending',              value: s.pending,              color: 'text-yellow-400' },
        { label: 'Avg Days',             value: s.avg_days ?? '—',      color: 'text-white' },
      ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white tracking-widest uppercase">My Stats</h1>
          <p className="text-gray-400 text-sm mt-1">{me.name} — your numbers vs the co-worker average</p>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {cards.map(c => <StatCard key={c.label} {...c} />)}
        </div>

        {isCad && me.daily && (
          <Card>
            <Label>Daily Average — This Month</Label>
            <p className="text-xs text-gray-500 mt-1">
              Completions per working day (weekdays minus approved time off) — excludes third-party jobs
            </p>
            <div className="flex items-end gap-8 mt-3">
              <BigNum value={me.daily.mine ?? 0} sub="You / day" />
              <BigNum value={me.daily.coworker_avg ?? 0} sub="Co-worker avg / day" dim />
              <p className="ml-auto text-xs text-gray-500 text-right">
                {Number(me.daily.weekdays) - Number(me.daily.my_days_off)} working days for you this month
                {Number(me.daily.my_days_off) > 0 && <><br />({me.daily.my_days_off} day{Number(me.daily.my_days_off) === 1 ? '' : 's'} off)</>}
              </p>
            </div>
          </Card>
        )}

        {isCad && <ForecastCard forecast={forecast} />}

        <Card>
          <Label>Completions — You vs Co-worker Average</Label>
          <p className="text-xs text-gray-500 mt-1">
            Last 6 months{isCad ? ' — excludes third-party jobs' : ''}. Co-worker average is other {isCad ? 'techs' : 'team members'}' completions divided by the number of active co-workers.
          </p>
          <DuoBarChart rows={me.months} aKey="mine" bKey="coworker_avg" aLabel="You" bLabel="Co-worker avg" />
        </Card>

        {isCad && (
          <Card>
            <Label>Third-Party Reviews — You vs Co-worker Completions</Label>
            <p className="text-xs text-gray-500 mt-1">
              Third-party jobs you reviewed each month, next to the average jobs your co-workers completed.
            </p>
            <DuoBarChart rows={me.months} aKey="my_reviews" bKey="coworker_avg" aLabel="Your reviews" bLabel="Co-worker avg completions" aColor="#a78bfa" />
          </Card>
        )}
      </div>
    </Layout>
  );
}

/* ── Sub-components ── */

function ForecastCard({ forecast }) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <Label>Pressure Forecast — Next 5 Days</Label>
          <p className="text-xs text-gray-500 mt-1">Field jobs scheduled in Site Tracker — each becomes CAD work on completion</p>
        </div>
        {forecast && (
          <div className="flex gap-5 text-right">
            <div>
              <p className="text-2xl font-black text-white">{forecast.total}</p>
              <p className="text-xs text-gray-500">next 5 days</p>
            </div>
            <div>
              <p className="text-2xl font-black text-orange-400">{forecast.overdue}</p>
              <p className="text-xs text-gray-500">past due in field</p>
            </div>
          </div>
        )}
      </div>
      {forecast ? (
        <BarChart
          rows={forecast.days.map(d => ({ month: d.label, count: d.count }))}
          max={Math.max(...forecast.days.map(d => Number(d.count)), 1)}
          color="#60a5fa"
        />
      ) : (
        <p className="text-gray-500 text-sm mt-4">Loading forecast from Site Tracker…</p>
      )}
    </Card>
  );
}

// Two bars per month — used for the "you vs co-worker average" comparisons
function DuoBarChart({ rows = [], aKey, bKey, aLabel, bLabel, aColor = '#c9a84c', bColor = '#60a5fa' }) {
  if (!rows.length) return <p className="text-gray-500 text-sm mt-4">No data yet.</p>;
  const max = Math.max(...rows.flatMap(r => [Number(r[aKey]) || 0, Number(r[bKey]) || 0]), 1);
  const BAR_MAX = 120;
  const h = (v) => Math.max(4, Math.round(((Number(v) || 0) / max) * BAR_MAX));
  return (
    <div>
      <div className="flex gap-5 mt-3">
        {[[aLabel, aColor], [bLabel, bColor]].map(([label, color]) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>
      <div className="flex items-end gap-4 mt-3">
        {rows.map(r => (
          <div key={r.month} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end justify-center gap-1">
              {[[aKey, aColor], [bKey, bColor]].map(([k, color]) => (
                <div key={k} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-gray-400 font-bold">{r[k] ?? 0}</span>
                  <div className="w-full rounded-t" style={{ height: `${h(r[k])}px`, backgroundColor: color }} />
                </div>
              ))}
            </div>
            <span className="text-xs text-gray-500 text-center leading-tight">{r.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="space-y-4">
      <h2 className="border-b pb-2 flex items-center justify-between" style={{ borderColor: '#c9a84c' }}>
        <span className="inline-block text-xs font-black uppercase tracking-[0.2em] rounded px-2.5 py-1" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>
          {title}
        </span>
        {action}
      </h2>
      {children}
    </div>
  );
}

function ReportButton({ label = 'Activity Report', onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded border transition hover:text-white"
      style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
    >
      {label}
    </button>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
      <p className={`text-3xl font-black ${color}`}>{value ?? 0}</p>
      <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{label}</p>
    </div>
  );
}

function BigNum({ value, sub, dim }) {
  return (
    <div>
      <p className={`font-black ${dim ? 'text-2xl text-gray-500' : 'text-4xl text-white'}`}>{value ?? 0}</p>
      <p className="text-xs text-gray-500 mt-1">{sub}</p>
    </div>
  );
}

function BarChart({ rows = [], max, color = '#c9a84c' }) {
  if (!rows.length) return <p className="text-gray-500 text-sm mt-4">No data yet.</p>;
  // Pixel heights — percentage heights don't resolve inside an auto-height flex
  // column, which left every bar collapsed to its minimum.
  const BAR_MAX = 120;
  return (
    <div className="flex items-end gap-4 mt-4">
      {rows.map(m => {
        const h = Math.max(4, Math.round((Number(m.count) / max) * BAR_MAX));
        return (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
            <span className="text-xs text-gray-400 font-bold">{m.count}</span>
            <div className="w-full rounded-t" style={{ height: `${h}px`, backgroundColor: color }} />
            <span className="text-xs text-gray-500 text-center leading-tight">{m.month}</span>
          </div>
        );
      })}
    </div>
  );
}

// '13:30' → '1:30p'
function compactTime(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')}${ap}` : `${h12}${ap}`;
}

const FLEX_BADGES = {
  office:  { text: 'Office — Open', color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  field:   { text: 'In Field',      color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  off:     { text: 'Off',           color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
  weekend: { text: 'Weekend',       color: '#6b7280', bg: 'rgba(107,114,128,0.10)' },
};

function FlexCalendar({ days = [] }) {
  if (!days.length) return <p className="text-gray-500 text-sm mt-4">No schedule data from Site Tracker.</p>;
  return (
    <div className="grid grid-cols-5 gap-3 mt-4">
      {days.map((d, i) => {
        const badge = FLEX_BADGES[d.availability] || FLEX_BADGES.off;
        return (
          <div key={d.date} className="border rounded-lg p-3 flex flex-col gap-2"
               style={{ borderColor: i === 0 ? '#c9a84c' : '#1e3a5f', backgroundColor: '#0a1524' }}>
            <p className="text-xs font-bold text-gray-300">
              {d.label}{i === 0 && <span className="text-gray-500 font-normal"> · today</span>}
            </p>
            <span className="text-[10px] font-black uppercase tracking-wider rounded px-1.5 py-0.5 self-start"
                  style={{ color: badge.color, backgroundColor: badge.bg }}>
              {badge.text}
            </span>
            {d.events.map((e, j) => {
              const time = !e.start_time && !e.end_time ? 'All day'
                : `${compactTime(e.start_time) ?? '…'}–${compactTime(e.end_time) ?? '…'}`;
              return (
                <div key={j} className="border rounded p-1.5" style={{ borderColor: '#1e3a5f' }}>
                  <p className="text-[11px] font-bold" style={{ color: e.is_field ? '#fb923c' : '#9ca3af' }}>
                    {e.title}
                  </p>
                  <p className="text-[10px] text-gray-500">{time}</p>
                  {e.site_name && <p className="text-[10px] text-gray-500 truncate" title={e.site_name}>{e.site_name}</p>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function AvgDaysGrid({ rows = [] }) {
  if (!rows.length) return <p className="text-gray-500 text-sm mt-3">No data yet.</p>;
  return (
    <div className="grid grid-cols-3 gap-4 mt-3">
      {rows.map(p => (
        <div key={p.name} className="border rounded p-3 text-center" style={{ borderColor: '#1e3a5f' }}>
          <p className="text-2xl font-black text-white">{p.avg_days ?? '—'}</p>
          <p className="text-xs text-gray-400 mt-1">days avg</p>
          <p className="text-xs text-gray-500 mt-1">{p.name}</p>
          <p className="text-xs text-gray-600">{p.total} jobs</p>
        </div>
      ))}
    </div>
  );
}
